import axios from 'axios';
import { getDeviceToken, setDeviceToken, clearDeviceToken } from './deviceToken';

const TOKEN_SAVE_FAILED_MESSAGE =
  'Could not save device credentials on this browser. Try a normal browser window (not private/incognito) or contact support.';

const TOKEN_MISSING_MESSAGE =
  'Device credentials were lost on this browser. Please log in again. If this keeps happening, contact support.';

// Calls the backend /register-device endpoint after a successful Firebase sign-in.

export async function registerDevice(idToken) {
  const existingToken = await getDeviceToken();

  try {
    const response = await axios.post(
      `${import.meta.env.VITE_BACKEND_URL}/register-device`,
      { deviceToken: existingToken || undefined },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      },
    );

    if (response.data?.deviceToken) {
      const saved = await setDeviceToken(response.data.deviceToken);
      if (!saved) {
        return {
          ok: false,
          rejected: true,
          code: 'DEVICE_TOKEN_SAVE_FAILED',
          message: TOKEN_SAVE_FAILED_MESSAGE,
        };
      }
      return { ok: true };
    }

    // Refresh path: server kept the existing session; verify token still in storage.
    const storedToken = await getDeviceToken();
    if (!storedToken || (existingToken && storedToken !== existingToken)) {
      return {
        ok: false,
        rejected: true,
        code: 'DEVICE_TOKEN_MISSING',
        message: TOKEN_MISSING_MESSAGE,
      };
    }

    return { ok: true };
  } catch (err) {
    const status = err?.response?.status;
    const data = err?.response?.data;

    // Device-policy rejection (limit reached or admin-revoked): drop any stale
    // token and block. Clearing the token lets a freed slot be reclaimed on the
    // next login attempt.
    if (status === 403 && (data?.code === 'DEVICE_LIMIT' || data?.code === 'DEVICE_REVOKED')) {
      await clearDeviceToken();
      return {
        ok: false,
        rejected: true,
        code: data.code,
        message:
          data?.message ||
          'This device cannot be used right now. Please contact support.',
      };
    }

    return {
      ok: false,
      rejected: true,
      code: data?.code || 'DEVICE_ERROR',
      message: data?.message || err?.message || 'Unable to verify this device.',
    };
  }
}
